import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { syncOpportunityFullFlagFromSeats } from "@shared/helpers/sync-opportunity-full-flag";
import { Controller, repo } from "remult";
import { GroupOpportunity } from "../group-opportunity";
import { OpportunityParticipant } from "../opportunity-participant";
import { Group } from "./entity";
export type GroupNoAuth = Awaited<
  ReturnType<typeof GroupMethods.getGroupDetailsNoAuth>
>;

@Controller("group-methods")
export class GroupMethods {
  @HyperBackendMethod({
    apiPrefix: "group-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async getGroupDetailsNoAuth(groupId: string) {
    const group = await repo(Group).findId(groupId, {
      include: {
        admin: true,
        organization: true,
        opportunities: {
          include: {
            opportunity: true,
          },
        },
      },
    });

    if (!group) return;

    const opportunity = group.opportunities?.[0]?.opportunity;

    return {
      organizationId: group.organization?.id,
      organizationName: group.organization?.name,
      adminId: group.admin?.id,
      opportunities: group.opportunities,
      opportunity: !opportunity
        ? undefined
        : pick(opportunity, [
            "id",
            "title",
            "hasTime",
            "hasStartTime",
            "startDate",
            "endDate",
            "imageId",
          ]),
      ...pick(group, ["id", "name", "size"]),
    };
  }

  @HyperBackendMethod({
    apiPrefix: "group-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async getGroupMembers(groupId: string) {
    const group = await repo(Group).findId(groupId, {
      include: {
        associations: {
          include: {
            association: {
              include: {
                user: true,
              },
            },
          },
        },
      },
    });

    if (!group) return [];

    return (
      group.associations
        ?.map((groupAssociation) => groupAssociation.association?.user?.name)
        .filter(Boolean) || []
    );
  }

  @HyperBackendMethod({
    apiPrefix: "group-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async approveGroup(whereToUpdate: any) {
    const opportunityInterested = await repo(OpportunityParticipant).findOne({
      where: whereToUpdate,
      include: {
        group: {
          include: {
            admin: true,
            organization: true,
          },
        },
        opportunity: true,
      },
    });

    if (!opportunityInterested?.group?.admin?.phoneNumber) return;

    // Add group to opportunity
    if (opportunityInterested.group) {
      await repo(GroupOpportunity).insert({
        groupId: opportunityInterested.group.id,
        opportunityId: opportunityInterested.opportunity?.id || "",
      });
    }

    // Keep the participant row as APPROVED so statistics count group.size members.
    await repo(OpportunityParticipant).updateMany({
      where: whereToUpdate,
      set: {
        status: "APPROVED",
        matchedAt: new Date(),
      },
    });

    const approvedOpportunity = opportunityInterested.opportunity;
    if (approvedOpportunity?.id) {
      await syncOpportunityFullFlagFromSeats(approvedOpportunity, false);
    }

    return {
      success: true,
      message: "Group approved and SMS sent to group admin",
    };
  }

  @HyperBackendMethod({
    apiPrefix: "group-methods",
    allowed: true,
    overrideSecurity: true,
  })
  static async rejectGroup(whereToUpdate: any) {
    const opportunityInterested = await repo(OpportunityParticipant).findOne({
      where: whereToUpdate,
      include: {
        group: {
          include: {
            admin: true,
            organization: true,
          },
        },
        opportunity: true,
      },
    });

    if (!opportunityInterested?.group?.admin?.phoneNumber) return;

    // Update the opportunity interested record to mark it as declined
    await repo(OpportunityParticipant).updateMany({
      where: {
        group: { $id: opportunityInterested.group.id },
        opportunity: { $id: opportunityInterested.opportunity?.id || "" },
      },
      set: {
        status: "DECLINED",
      },
    });

    const rejectedOpportunity = opportunityInterested.opportunity;
    if (rejectedOpportunity?.id) {
      await syncOpportunityFullFlagFromSeats(rejectedOpportunity, true);
    }

    return {
      success: true,
      message: "Group rejected and SMS sent to group admin",
    };
  }
}

function pick<T extends Record<any, any>, W extends keyof T>(
  from: T,
  what: W[],
) {
  return what.reduce((prev, curr) => {
    return {
      ...prev,
      [curr]: from[curr],
    };
  }, {}) as {
    [K in W]: T[K];
  };
}
